import { graphql } from "gatsby"
import React from "react"
import { Col, Container, Row } from "react-bootstrap"
import { EducationItem, PageLayout, PageTitle, WorkHistory } from "../components"
import { SEO, Utils, useTranslation } from "../utils"

const CV_SECTIONS = [
  { id: "experience", icon: "💼" },
  { id: "education", icon: "🎓" },
  { id: "certifications", icon: "🏅" },
]

const CvSideNav = ({ sections, activeId }) => {
  const { t } = useTranslation()
  const handleClick = (e, id) => {
    e.preventDefault()
    const el = document.getElementById(id)
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" })
  }
  return (
    <nav className="cv-side-nav">
      {sections.map(({ id, icon }) => (
        <a
          key={id}
          href={`#${id}`}
          className={`cv-side-nav-item${activeId === id ? " active" : ""}`}
          onClick={e => handleClick(e, id)}
          title={t(`cv.${id}`)}
        >
          <span className="cv-side-nav-icon">{icon}</span>
          <span className="cv-side-nav-label">{t(`cv.${id}`)}</span>
        </a>
      ))}
    </nav>
  )
}

export default ({ data }) => {
  const { t } = useTranslation()
  const history = data.workHistory.edges || []
  const education = data.educationHistory.edges || []
  const images = data.allFile.edges || []
  const imageMap = Utils.getImageMap(images, /\/[work].*\/|$/)
  const [activeId, setActiveId] = React.useState("experience")

  React.useEffect(() => {
    const observers = CV_SECTIONS.map(({ id }) => {
      const el = document.getElementById(id)
      if (!el) return null
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveId(id) },
        { rootMargin: "-40% 0px -55% 0px" }
      )
      obs.observe(el)
      return obs
    }).filter(Boolean)
    return () => observers.forEach(o => o.disconnect())
  }, [])

  return (
    <PageLayout pageName="resume">
      <SEO title={t("cv.title")} />
      <PageTitle title={t("cv.title")} />

      {/* Bannière téléchargement */}
      <Container className="mb-5">
        <div className="cv-download-banner d-flex justify-content-between align-items-center">
          <small className="text-muted">{t("cv.updated")}</small>
          <a
            href="cv_regis_atemengue.pdf"
            target="_blank"
            rel="noopener noreferrer"
            download
            className="cv-download-btn"
          >
            {t("cv.download")}
          </a>
        </div>
      </Container>

      <div className="cv-layout">
        <CvSideNav sections={CV_SECTIONS} activeId={activeId} />

        <div className="cv-main">
          {/* Expérience */}
          <Container id="experience" className="mb-5 cv-anchor">
            <h5 className="cv-section-title mb-4">{t("cv.experience")}</h5>
            <div className="cv-timeline">
              {history.map(({ node }) => (
                <WorkHistory
                  key={node.id}
                  frontmatter={node.frontmatter}
                  image={imageMap[node.fields.slug]}
                  html={node.html}
                />
              ))}
            </div>
          </Container>

          {/* Formation */}
          <Container id="education" className="mb-5 cv-anchor">
            <h5 className="cv-section-title mb-4">{t("cv.education")}</h5>
            <div className="cv-timeline">
              {education.map(({ node }) => (
                <EducationItem
                  key={node.id}
                  frontmatter={node.frontmatter}
                />
              ))}
            </div>
          </Container>

          {/* Certifications */}
          <Container id="certifications" className="mb-5 cv-anchor">
            <h5 className="cv-section-title mb-4">{t("cv.certifications")}</h5>
            <div className="cv-empty-state">
              <span className="cv-empty-state-icon">🏅</span>
              <p className="cv-empty-state-text">{t("cv.certifications_empty")}</p>
            </div>
          </Container>
        </div>
      </div>
    </PageLayout>
  )
}

export const query = graphql`
  query {
    workHistory: allMarkdownRemark(
      filter: { fileAbsolutePath: { regex: "/work/" } }
      sort: { fields: [frontmatter___endDate], order: DESC }
    ) {
      edges {
        node {
          id
          html
          frontmatter {
            company
            location
            position
            tags
            startDate(formatString: "MMM YYYY")
            endDate(formatString: "MMM YYYY")
          }
          fields {
            slug
          }
        }
      }
    }
    educationHistory: allMarkdownRemark(
      filter: { fileAbsolutePath: { regex: "/education/" } }
      sort: { fields: [frontmatter___endDate], order: DESC }
    ) {
      edges {
        node {
          id
          frontmatter {
            institution
            location
            degree
            field
            tags
            startDate(formatString: "MMM YYYY")
            endDate(formatString: "MMM YYYY")
          }
        }
      }
    }
    allFile(
      filter: {
        extension: { eq: "png" }
        relativePath: { regex: "/company/" }
        relativeDirectory: { regex: "/content/work/" }
      }
    ) {
      edges {
        node {
          childImageSharp {
            fluid(maxWidth: 80) {
              ...GatsbyImageSharpFluid
            }
          }
          relativePath
        }
      }
    }
  }
`
